import { useEffect, useState } from "react";

import { AnchoredDialog, Button, Checkbox, Row } from "@narmi/design_system";
import { Localized, useLocalization } from "@fluent/react";

import styles from "./DocumentFilterAnchoredDialog.module.scss";

import type { AnchoredDialogProps } from "@narmi/design_system/dist/types/AnchoredDialog";

// The Item's key can be thought of as a candidate key which when represented as a string
// is either a composite of two keys or a single key, in this case a key is a Document's `type`.
type Item = { key: string[] | string; name: string };

const convertKeyToString = (key: string[] | string) =>
  Array.isArray(key) ? key.join("-") : key;

const getTypes = (key: string[] | string): string[] =>
  Array.isArray(key) ? key : [key];

const DocumentFilterAnchoredDialog = ({
  heading,
  items,
  activeFilters,
  onApply,
  isOpen,
  setIsOpen,
  trigger,
}: {
  heading: string;
  items: Item[];
  activeFilters: string[];
  onApply: (checkedItemKeys: string[]) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  trigger: AnchoredDialogProps["trigger"];
}) => {
  const { l10n } = useLocalization();
  const [checkboxState, setCheckboxState] = useState<Set<string>>(
    () => new Set(activeFilters)
  );

  useEffect(() => {
    setCheckboxState(new Set(activeFilters));
  }, [activeFilters]);

  return (
    <div className={styles["document-filter-anchored-dialog-wrapper"]}>
      <AnchoredDialog
        renderHeader={() => (
          <Row alignItems="center" justifyContent="space-between">
            <span className={styles["document-popover-heading"]}>
              {heading}
            </span>
            {checkboxState.size > 0 ? (
              <button
                onClick={() => setCheckboxState(new Set())}
                className={styles["document-popover-clear-all"]}
                aria-label={l10n.getString(
                  "document-filter-popover-clear-all",
                  null,
                  "Clear all"
                )}
              >
                <Localized id="document-filter-popover-clear-all">
                  Clear all
                </Localized>
              </button>
            ) : (
              <button
                onClick={() =>
                  setCheckboxState(
                    new Set(items.flatMap((item) => getTypes(item.key)))
                  )
                }
                className={styles["document-popover-clear-all"]}
                aria-label={l10n.getString(
                  "document-filter-popover-select-all",
                  null,
                  "Select all"
                )}
              >
                <Localized id="document-filter-popover-select-all">
                  Select all
                </Localized>
              </button>
            )}
          </Row>
        )}
        renderFooter={() => (
          <Row justifyContent="end" alignItems="center">
            <Button
              onClick={() => onApply([...checkboxState])}
              size="xs"
              ariaLabel={l10n.getString(
                "document-filter-popover-apply",
                null,
                "Apply"
              )}
            >
              <Localized id="document-filter-popover-apply">Apply</Localized>
            </Button>
          </Row>
        )}
        isOpen={isOpen}
        onUserDismiss={() => setIsOpen(false)}
        trigger={trigger}
      >
        <div className={styles["document-popover"]}>
          <div className={styles["document-popover-list"]}>
            {items.map((item) => {
              const types = getTypes(item.key);
              // If one is checked, the other will be checked as well, since they're under
              // the same filter type.
              const isChecked = types.some((type) => checkboxState.has(type));
              return (
                <Checkbox
                  key={convertKeyToString(item.key)}
                  label={item.name}
                  kind="condensed"
                  checked={isChecked}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setCheckboxState((prevState) => {
                      const newState = new Set(prevState);
                      if (event.target.checked) {
                        types.forEach((type) => newState.add(type));
                      } else {
                        types.forEach((type) => newState.delete(type));
                      }
                      return newState;
                    });
                  }}
                />
              );
            })}
          </div>
        </div>
      </AnchoredDialog>
    </div>
  );
};

export default DocumentFilterAnchoredDialog;
